"use client";
import React, { useState, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";
import Link from "next/link";
import ProjectCard from "./ProjectCard";
import { categories, Project } from "./ProjectData";

interface ProjectsProps {
  projects: Project[];
}

const Projects = ({ projects }: ProjectsProps) => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [visibleRows, setVisibleRows] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const projectsPerRow = 4;
  const maxVisibleRows = 3; // Changed to 3 rows for large devices (12 projects total)

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const filteredProjects = selectedCategory === "All"
    ? projects
    : projects.filter(project => project.categories.includes(selectedCategory));

  const totalRows = Math.ceil(filteredProjects.length / projectsPerRow);
  const visibleProjects = filteredProjects.slice(0, visibleRows * projectsPerRow);

  const handleShowMore = () => {
    setVisibleRows(prev => prev + 1);
  };

  return (
    <section id="projects" className="relative z-10 py-16 md:py-20 lg:py-28">
      <div className="container">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold !leading-tight text-black dark:text-white sm:text-4xl md:text-[45px]">
            Real Projects, Real Impact
          </h2>
          <p className="text-base !leading-relaxed text-body-color md:text-lg">
            Explore the solutions we&apos;ve crafted to solve real-world challenges — with creativity, intelligence, and precision.
          </p>
        </div>

        {/* Filters and View All Button */}
        <div className="mb-10">
          {/* Large devices: Only Filters */}
          <div className="hidden lg:flex lg:items-center lg:justify-between lg:gap-4">
            <div className="flex flex-wrap gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setVisibleRows(1); // Reset visible rows when category changes
                  }}
                  className={`rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 ${selectedCategory === category
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-dark dark:text-gray-300 dark:hover:bg-gray-800"
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile and tablet: Only Filters */}
          <div className="lg:hidden">
            <div className="flex flex-wrap gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setVisibleRows(1); // Reset visible rows when category changes
                  }}
                  className={`rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 ${selectedCategory === category
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-dark dark:text-gray-300 dark:hover:bg-gray-800"
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center">
          <div className="mx-auto">
            <div className="flex items-center justify-center">
              <div className="flex flex-wrap items-center justify-center gap-10">
                {visibleProjects.map((project, index) => (
                  <div key={index} className="">
                    <ProjectCard project={project} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Buttons Container - for both Show More and View All Projects */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            {/* Show More/Less Button - visible when there are more rows to show */}
            {visibleRows > 1 || totalRows > 1 ? (
              <button
                onClick={() => visibleRows > 1 ? setVisibleRows(1) : handleShowMore()}
                className="group flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary to-primary/80 px-8 py-3 text-white font-semibold transition-all duration-300 hover:from-primary/90 hover:to-primary/70 hover:shadow-lg"
              >
                {visibleRows > 1 ? "Show Less" : "Show More"}
                <svg 
                  className={`w-4 h-4 transition-transform duration-300 ${visibleRows > 1 ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 17l4 4m0 0l4-4m-4 4V3" />
                </svg>
              </button>
            ) : null}

            {/* View All Projects Button - always visible */}
            <Link
              href="/projects"
              className="group inline-flex items-center gap-2 bg-primary/10 hover:bg-primary/20 dark:bg-primary/20 dark:hover:bg-primary/30 text-primary dark:text-white font-semibold px-8 py-3 rounded-lg transition-all duration-200 border border-primary/20"
            >
              View All Projects
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;